import { useState } from 'react';

interface ImageUploadProps {
  onImagesSelected: (images: File[]) => void;
  maxImages?: number;
}

export function ImageUpload({ onImagesSelected, maxImages = 4 }: ImageUploadProps) {
  const [selectedImages, setSelectedImages] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (selectedImages.length + validFiles.length > maxImages) {
      alert(`Você pode enviar no máximo ${maxImages} imagens`);
      return;
    }

    const newImages = [...selectedImages, ...validFiles];
    setSelectedImages(newImages);
    onImagesSelected(newImages);

    // Create preview URLs
    const newUrls = validFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newUrls]);
  };

  const removeImage = (index: number) => {
    const newImages = selectedImages.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    
    // Revoke the URL to free memory
    URL.revokeObjectURL(previewUrls[index]);
    
    setSelectedImages(newImages);
    setPreviewUrls(newUrls);
    onImagesSelected(newImages);
  };

  return (
    <div className="space-y-3">
      {/* Upload Button */}
      {selectedImages.length < maxImages && (
        <label className="block cursor-pointer">
          <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-teal-500 transition-colors bg-slate-50">
            <i className="ri-image-add-line text-4xl text-slate-400 mb-2"></i>
            <p className="text-sm font-medium text-slate-600">Clique para adicionar imagens</p>
            <p className="text-xs text-slate-400 mt-1">PNG, JPG até 5MB ({selectedImages.length}/{maxImages})</p>
          </div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
      )}

      {/* Image Previews */}
      {previewUrls.length > 0 && (
        <div className={`grid gap-3 ${
          previewUrls.length === 1 ? 'grid-cols-1' :
          previewUrls.length === 2 ? 'grid-cols-2' :
          'grid-cols-2'
        }`}>
          {previewUrls.map((url, index) => (
            <div key={index} className="relative group">
              <img
                src={url}
                alt={`Preview ${index + 1}`}
                className="w-full h-48 object-cover rounded-xl"
              />
              <button
                onClick={() => removeImage(index)}
                className="absolute top-2 right-2 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:scale-110"
              >
                <i className="ri-close-line text-white"></i>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
